import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {
  CreateScribeArtifactInput,
  DeleteScribeArtifactInput,
  DeleteScribeArtifactResult,
  ListScribeArtifactsOptions,
  ScribeArtifact,
  ScribeArtifactResourceOptions,
  ScribeArtifactsListPayload,
} from './types'

interface ScribeArtifactPayload {
  artifact: ScribeArtifact
}

export class ScribeArtifactsRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ScribeArtifactsRequestError'
    this.status = status
  }
}

export function buildScribeArtifactsPath({resourceType, resourceId, type}: ListScribeArtifactsOptions): string {
  const params = new URLSearchParams({
    resource_type: resourceType,
    resource_id: String(resourceId),
  })
  if (type) params.set('type', type)

  return `/scribe/artifacts?${params.toString()}`
}

/** Builds the path to a single Scribe artifact, identified by its `number`. */
export function buildScribeArtifactPath(
  number: number,
  {resourceType, resourceId}: ScribeArtifactResourceOptions,
): string {
  const params = new URLSearchParams({
    resource_type: resourceType,
    resource_id: String(resourceId),
  })

  return `/scribe/artifacts/${number}?${params.toString()}`
}

export async function listScribeArtifacts(options: ListScribeArtifactsOptions): Promise<ScribeArtifact[]> {
  const response = await verifiedFetchJSON(buildScribeArtifactsPath(options), {signal: options.signal})
  await assertOk(response, 'Unable to list Scribe artifacts')

  const payload = (await response.json()) as ScribeArtifactsListPayload | ScribeArtifact[]
  return Array.isArray(payload) ? payload : payload.artifacts
}

export async function createScribeArtifact({
  resourceType,
  resourceId,
  signal,
  name,
  description,
  body,
  type,
}: CreateScribeArtifactInput): Promise<ScribeArtifact> {
  const response = await verifiedFetchJSON(buildScribeArtifactsPath({resourceType, resourceId}), {
    method: 'POST',
    signal,
    body: {
      name,
      description,
      body,
      type,
    },
  })
  await assertOk(response, 'Unable to create Scribe artifact')

  const payload = (await response.json()) as ScribeArtifactPayload | ScribeArtifact
  return 'artifact' in payload ? payload.artifact : payload
}

export async function deleteScribeArtifact({
  resourceType,
  resourceId,
  number,
  signal,
}: DeleteScribeArtifactInput): Promise<DeleteScribeArtifactResult> {
  const response = await verifiedFetchJSON(buildScribeArtifactPath(number, {resourceType, resourceId}), {
    method: 'DELETE',
    signal,
  })
  await assertOk(response, 'Unable to delete Scribe artifact')

  let payload: Partial<DeleteScribeArtifactResult>
  try {
    payload = (await response.json()) as Partial<DeleteScribeArtifactResult>
  } catch {
    payload = {}
  }
  return {number: payload.number ?? number, deleted: payload.deleted ?? true}
}

async function assertOk(response: Response, message: string): Promise<void> {
  if (response.ok) return

  throw new ScribeArtifactsRequestError(message, response.status)
}
